import { Component, inject, input, OnInit, signal, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommentDto, SaveCommentDto, SaveProjectCommentDto } from '@models/comment';
import { CommentService } from 'src/app/services/comment.service';
import { MatTableModule } from '@angular/material/table';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { ModelType } from '@shared/constants/model-type.enum';
import { LoggedInUserService } from 'src/app/services/auth/logged-in-user-service';

@Component({
  selector: 'app-comments',
  standalone: true,
  imports: [MatTableModule, MatFormField, MatFormFieldModule, MatInputModule, MatButtonModule, MatProgressSpinnerModule, ReactiveFormsModule, CommonModule],
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss'
})
export class CommentsComponent implements OnInit{
  treeId = input.required<number>();
  projectId = input.required<number>();
  folderName = input.required<string>();
  modelType = input.required<ModelType>();
  isProjectView = input.required<boolean>();
  displayedColumns: string[] = ['date', 'time', 'name', 'comments'];
  enteredText = new FormControl('', [Validators.required, Validators.minLength(5)]);
  commentData: CommentDto[] = [];
  loggedInUser: string = ''; 
  isLoading: boolean = false;
  errorMessage = signal('');

  private commentService = inject(CommentService);
  private loggedInUserService = inject(LoggedInUserService);

  ngOnInit() : void {
    this.getLoggedInUser();
    if (this.isProjectView()){
      this.getProjectCommentData();
    }
    else {
      this.getCommentData();
    }    
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['folderName'] && !changes['folderName'].isFirstChange()) {
      this.getCommentData();      
    }

    if (changes['projectId'] && !changes['projectId'].isFirstChange()) {
      this.getProjectCommentData();
    }
  }

  getCommentData() {
    this.isLoading = true;
    return this.commentService.getCommentData(this.treeId(), this.modelType())
      .subscribe(resp => {
        this.isLoading = false;        
        this.commentData = resp;
      });
  }

  getProjectCommentData() {
    this.isLoading = true;
    return this.commentService.getProjectCommentData(this.projectId(), this.modelType())
      .subscribe(resp => {
        this.isLoading = false;        
        this.commentData = resp;        
      });
  }

  getLoggedInUser() {
    this.loggedInUserService.currentUser$.subscribe(res => {
      this.loggedInUser = res?.userName!
    })
  }

  onSaveClick() {
    if (this.enteredText.invalid) {
      this.errorMessage.set('Please enter a valid, non-empty comment.');
      return;
    }

    let comment: CommentDto = {
      id: 0,
      comments: this.enteredText.value!,
      createdDateTime: new Date(),
      name: this.loggedInUser
    }

    if (this.isProjectView()){
      this.saveProjectCommentData(comment)
    }

    else {
      this.saveCommentData(comment)
    }    
  }

  saveCommentData(comment: CommentDto) : void {
    let saveCommentDto: SaveCommentDto = {
      comment: comment,
      treeId: this.treeId()
    }

    this.commentService.saveCommentData(saveCommentDto, this.modelType())
      .subscribe((res)  => {
        if(res.isSuccess) {
          this.getCommentData();
        } 
        
      });
  }

  saveProjectCommentData(comment: CommentDto) : void {
    let saveProjectCommentDto: SaveProjectCommentDto = {
      comment: comment,
      projectId: this.projectId()
    }
    
    this.commentService.saveProjectCommentData(saveProjectCommentDto, this.modelType())
      .subscribe((res) => {
        if(res.isSuccess) {
          this.getProjectCommentData();
        }
      });
  }

  onResetClick() {
    this.enteredText.reset();
  }
}

